import { InputHTMLAttributes, ReactNode, Ref, forwardRef, useId } from "react";
import MaskedInput from "react-text-mask";
import { cx } from "../../utils/cx";
import styles from "./Input.module.scss";

/** Compatible with `react-text-mask` / `text-mask-addons` (e.g. `currencyMask`). */
export type InputMask =
    | Array<string | RegExp>
    | ((value: string) => Array<string | RegExp>)
    | boolean;

export interface InputProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
    /**
     * Floating label rendered both as the placeholder (centered, 15px) when
     * the input is empty and as a small label (12px) above the value when
     * the input is filled or focused. Required — drives the entire visual
     * pattern of the Figma `Input` component.
     */
    label: string;
    /**
     * Helper text rendered below the field. When `error` is a string, that
     * message replaces this one and the field is rendered in error style.
     */
    helperText?: string;
    /**
     * Error state. `true` marks the field as invalid without a custom
     * message; passing a string also sets the helper-text content.
     */
    error?: boolean | string;
    /** Marks the field as required and appends `*` to the label. */
    required?: boolean;
    /** Optional icon rendered inside the field, before the input. */
    iconLeft?: ReactNode;
    /** Optional icon rendered inside the field, after the input. */
    iconRight?: ReactNode;
    /** Text adornment rendered before the input (e.g. `$`, `https://`). */
    prefix?: string;
    /** Text adornment rendered after the input (e.g. `kg`, `.com`). */
    suffix?: string;
    /** Stretches the field to 100% of its container. */
    fullWidth?: boolean;
    /**
     * `floating` (default) = Figma floating label. `inline` = native `placeholder`
     * + value vertically centered (e.g. menu search without label jumping up).
     */
    labelBehavior?: "floating" | "inline";
    /**
     * `compact` = 40px field height, bordered surface — for menus (e.g.
     * `StoreSelector` search). Default keeps the 52px floating field.
     */
    density?: "default" | "compact";
    /**
     * Optional `react-text-mask` mask. When omitted, the field is a plain
     * native input (existing behavior). When set, input formatting is
     * constrained without changing the DS field chrome (label, prefix, etc.).
     */
    mask?: InputMask;
}

const assignRef = <T,>(ref: Ref<T> | undefined, value: T) => {
    if (!ref) return;
    if (typeof ref === "function") {
        ref(value);
        return;
    }
    // eslint-disable-next-line no-param-reassign
    (ref as { current: T }).current = value;
};

/**
 * Treez DS — Input / TextField (Wave 1).
 *
 * Source: Figma `Input` component (file `qgFv3OQga6RQ4luvSFuGFP`).
 *
 * Visual pattern: filled (no border) at rest, 2px black border + lighter bg
 * when active/focused. With `labelBehavior="floating"` (default), the label
 * travels from center → top when the field has a value or is focused.
 * With `labelBehavior="inline"`, use a native placeholder and keep the value
 * vertically centered (menu search).
 *
 * Click handling: the entire field is wrapped in a `<label htmlFor>` so any
 * click — even on empty space, prefix, suffix or icons — focuses the input.
 *
 * Accessibility:
 * - The wrapper `<label>` provides the accessible name via `htmlFor`.
 * - `aria-invalid` and `aria-describedby` are set automatically.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
    {
        label,
        helperText,
        error,
        required,
        iconLeft,
        iconRight,
        prefix,
        suffix,
        fullWidth = false,
        density = "default",
        labelBehavior = "floating",
        mask,
        id,
        className,
        disabled,
        ...rest
    },
    ref,
) {
    const { placeholder: placeholderProp, value, ...inputRest } = rest;
    const reactId = useId();
    const inputId = id ?? `ds-input-${reactId}`;
    const messageId = `${inputId}-msg`;

    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : helperText;
    const placeholder =
        labelBehavior === "inline"
            ? placeholderProp ?? `${label}${required ? " *" : ""}`
            : " ";

    const sharedInputProps = {
        ...inputRest,
        "aria-describedby": message ? messageId : undefined,
        "aria-invalid": hasError || undefined,
        className: styles.input,
        disabled,
        id: inputId,
        placeholder,
        required,
        ...(value !== undefined
            ? { value: value == null ? "" : String(value) }
            : {}),
    };

    const control =
        mask != null ? (
            <MaskedInput
                {...sharedInputProps}
                mask={mask}
                render={(maskRef, props) => (
                    <input
                        {...props}
                        ref={node => {
                            maskRef(node);
                            assignRef(ref, node);
                        }}
                    />
                )}
            />
        ) : (
            <input {...sharedInputProps} ref={ref} />
        );

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                className,
            )}
            data-density={density === "compact" ? "compact" : undefined}
            data-disabled={disabled || undefined}
            data-error={hasError || undefined}
            data-label-behavior={
                labelBehavior === "inline" ? "inline" : undefined
            }
            data-ui="input"
        >
            <label className={styles.field} htmlFor={inputId}>
                {labelBehavior === "inline" ? (
                    <span className={styles.visuallyHidden}>
                        {label}
                        {required ? " *" : ""}
                    </span>
                ) : null}
                {iconLeft && (
                    <span aria-hidden className={styles.iconLeft}>
                        {iconLeft}
                    </span>
                )}
                {prefix && <span className={styles.prefix}>{prefix}</span>}

                <span className={styles.labelInputWrap}>
                    {control}
                    {labelBehavior === "floating" ? (
                        <span className={styles.label}>
                            {label}
                            {required && " *"}
                        </span>
                    ) : null}
                </span>

                {suffix && <span className={styles.suffix}>{suffix}</span>}
                {iconRight && (
                    <span className={styles.iconRight}>{iconRight}</span>
                )}
            </label>

            {message && (
                <div className={styles.helper} id={messageId}>
                    {message}
                </div>
            )}
        </div>
    );
});

Input.displayName = "Input";
